import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowUpRightIcon, Trash2Icon } from 'lucide-react';
import { tenantApi } from '../lib/tenant-api';
import MarkdownRenderer from './MarkdownRenderer';
import { loadJSON, saveJSON, removeKey } from '../lib/chatStorage';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

export interface AIAssistantChatProps {
  contextKey: string;
  contextParams?: { date_from?: string; date_to?: string };
  suggestions?: string[];
  initialMessage?: string;
  /** Heading shown in the empty state. Default: "Start a conversation" */
  title?: string;
  /** Sub-text shown under the heading. Default: "Ask me anything…" */
  subtitle?: string;
  /** Name shown in the header bar. Default: "AI Assistant" */
  name?: string;
  /** Description next to the name. e.g. "Active · Engagement context" */
  description?: string;
  /** Additional CSS classes for the root container */
  className?: string;
  /** Whether the chat is currently visible/open — drives auto-focus of the input. */
  isOpen?: boolean;
}

// ---------------------------------------------------------------------------
// Shared icon
// ---------------------------------------------------------------------------

export const AiIcon: React.FC = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="40" height="40" rx="20" fill="transparent" />
    <g clipPath="url(#clip0_ai)">
      <path d="M18.3136 24.9966H21.6919C21.8019 23.9949 22.3128 23.1682 23.1419 22.2657C23.2361 22.1641 23.8353 21.5432 23.9061 21.4549C24.4947 20.7196 24.8636 19.833 24.9704 18.8972C25.0772 17.9614 24.9174 17.0145 24.5096 16.1655C24.1019 15.3165 23.4625 14.6 22.6654 14.0984C21.8682 13.5968 20.9455 13.3306 20.0037 13.3304C19.0618 13.3302 18.1391 13.596 17.3417 14.0972C16.5443 14.5985 15.9047 15.3148 15.4965 16.1636C15.0883 17.0124 14.9282 17.9593 15.0346 18.8951C15.141 19.8309 15.5095 20.7177 16.0978 21.4532C16.1694 21.5424 16.7703 22.1641 16.8628 22.2649C17.6928 23.1682 18.2036 23.9949 18.3136 24.9966V24.9966ZM21.6694 26.6632H18.3361V27.4966H21.6694V26.6632ZM14.7978 22.4966C14.0129 21.516 13.5211 20.3336 13.3788 19.0857C13.2366 17.8378 13.4498 16.5751 13.9938 15.443C14.5379 14.311 15.3906 13.3556 16.4539 12.687C17.5172 12.0185 18.7477 11.6639 20.0037 11.6641C21.2597 11.6643 22.4901 12.0193 23.5531 12.6882C24.6162 13.3571 25.4686 14.3127 26.0123 15.4449C26.556 16.5772 26.7688 17.8399 26.6261 19.0878C26.4835 20.3357 25.9913 21.5179 25.2061 22.4982C24.6894 23.1416 23.3361 24.1632 23.3361 25.4132V27.4966C23.3361 27.9386 23.1605 28.3625 22.848 28.6751C22.5354 28.9876 22.1115 29.1632 21.6694 29.1632H18.3361C17.8941 29.1632 17.4702 28.9876 17.1576 28.6751C16.845 28.3625 16.6694 27.9386 16.6694 27.4966V25.4132C16.6694 24.1632 15.3153 23.1416 14.7978 22.4966ZM20.8361 18.3332H22.9194L19.1694 23.3332V19.9999H17.0861L20.8361 14.9966V18.3341V18.3332Z" fill="white" />
    </g>
    <defs>
      <clipPath id="clip0_ai">
        <rect width="20" height="20" fill="white" transform="translate(10 10)" />
      </clipPath>
    </defs>
  </svg>
);

// ---------------------------------------------------------------------------
// Typing indicator
// ---------------------------------------------------------------------------

const TypingIndicator: React.FC = () => (
  <div className="flex items-start gap-2.5">
    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center mt-0.5">
      <AiIcon />
    </div>
    <div className="bg-gray-100 rounded-tr-xl rounded-b-xl px-4 py-3 flex items-center gap-1.5">
      <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.3s]" />
      <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.15s]" />
      <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" />
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Message bubble
// ---------------------------------------------------------------------------

const MessageBubble: React.FC<{ message: ChatMessage }> = ({ message }) => {
  const isUser = message.role === 'user';

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] bg-indigo-600 text-white rounded-tl-xl rounded-b-xl px-4 py-2.5 text-sm leading-relaxed shadow-sm">
          {message.content}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2.5">
      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center mt-0.5">
        <AiIcon />
      </div>
      <div className="max-w-[85%] bg-gray-100 rounded-tr-xl rounded-b-xl px-4 py-3 text-sm shadow-sm">
        <MarkdownRenderer content={message.content} className="text-gray-800" />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component — fills w-full h-full of its container
// ---------------------------------------------------------------------------

const AIAssistantChat: React.FC<AIAssistantChatProps> = ({
  contextKey,
  contextParams,
  suggestions = [],
  initialMessage,
  title = '',
  subtitle = '',
  name = 'AI Assistant',
  description,
  className,
  isOpen = true,
}) => {
  // Persist the transcript per context so it survives a page reload.
  const storageKey = `aichat:v1:${contextKey}`;
  const [messages, setMessages] = useState<ChatMessage[]>(() => loadJSON<ChatMessage[]>(storageKey, []));
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const sentInitialRef = useRef<string | undefined>(undefined);
  const didInitScrollRef = useRef(false);

  // Save on every change (cap the stored history to keep it lightweight).
  useEffect(() => {
    saveJSON(storageKey, messages.slice(-100));
  }, [messages, storageKey]);

  // Conversation anchoring: keep focus on the newest exchange. On every new message
  // (question or reply) scroll the latest user message up as far as the content
  // allows — the browser clamps it, so there's no reserved blank space.
  const anchorIndex = (() => {
    for (let i = messages.length - 1; i >= 0; i--) if (messages[i].role === 'user') return i;
    return -1;
  })();

  useLayoutEffect(() => {
    const c = scrollRef.current;
    if (!c) return;
    if (!didInitScrollRef.current) {
      didInitScrollRef.current = true;
      c.scrollTop = c.scrollHeight; // first render → show newest
      return;
    }
    const a = anchorRef.current;
    c.scrollTop = a ? Math.max(0, a.offsetTop - 12) : c.scrollHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, isLoading]);

  // Focus the input when the chat opens, unless a message is sending/awaiting a reply.
  useEffect(() => {
    if (isOpen && !isLoading) {
      inputRef.current?.focus();
    }
  }, [isOpen, isLoading]);

  // Auto-send initialMessage
  useEffect(() => {
    if (initialMessage && sentInitialRef.current !== initialMessage) {
      sentInitialRef.current = initialMessage;
      setTimeout(() => sendMessage(initialMessage), 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMessage]);

  const sendMessage = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;

    setError(null);

    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: trimmed,
    };
    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    setIsLoading(true);

    try {
      const history = messages.map((m) => ({ role: m.role, content: m.content }));

      const result = await tenantApi.queryAIAssistant({
        message: trimmed,
        context_key: contextKey,
        context_params: contextParams,
        conversation_history: history,
      });

      const aiMsg: ChatMessage = {
        id: `ai-${Date.now()}`,
        role: 'assistant',
        content: result.response,
      };
      setMessages((prev) => [...prev, aiMsg]);
    } catch {
      setError('Something went wrong. Please try again.');
      setMessages((prev) => prev.filter((m) => m.id !== userMsg.id));
      setInputValue(trimmed);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  const clearChat = () => {
    setMessages([]);
    removeKey(storageKey);
    setError(null);
    inputRef.current?.focus();
  };

  const isEmpty = messages.length === 0 && !isLoading;

  return (
    <div className={`w-full h-full flex flex-col ${className ?? ''}`}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white flex-shrink-0">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center">
          <AiIcon />
        </div>
        <div className="flex flex-col">
          <span className="text-base font-semibold text-gray-900">{name}</span>
          {description && (
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
              <span className="text-xs text-gray-500">Active · {description}</span>
            </div>
          )}
        </div>

        {/* {description === 'Engagement context' && (
          <div className="ml-auto sm:block hidden">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-600 border border-indigo-100">
              <SparklesIcon className="h-3 w-3" />
              Powered by LAWMA AI
            </span>
          </div>
        )} */}
      </div>

      {/* Content */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-6">
        {isEmpty ? (
          <div className="flex flex-col items-center text-center pt-8">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center mb-5">
              <AiIcon />
            </div>
            {title && <h3 className="text-lg font-bold text-gray-900">{title}</h3>}
            {subtitle && <p className="mt-2 text-sm text-gray-600 max-w-md">{subtitle}</p>}

            {/* Suggestion cards — responsive grid */}
            {suggestions.length > 0 && (
              <div className="mt-8 w-full grid grid-cols-1 max-w-[500px] gap-3">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => sendMessage(s)}
                    className="w-full flex items-center justify-between px-4 py-3.5 text-sm text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors text-left rounded"
                  >
                    <span className="line-clamp-2">{s}</span>
                    <ArrowUpRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0 ml-3" />
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            {anchorIndex > 0 && (
              <div className="space-y-4">
                {messages.slice(0, anchorIndex).map((msg) => (
                  <MessageBubble key={msg.id} message={msg} />
                ))}
              </div>
            )}
            {/* The latest turn — scrolled to the top on each new message. */}
            <div ref={anchorRef} className={`space-y-4 ${anchorIndex > 0 ? 'pt-4' : ''}`}>
              {(anchorIndex >= 0 ? messages.slice(anchorIndex) : messages).map((msg) => (
                <MessageBubble key={msg.id} message={msg} />
              ))}
              {isLoading && <TypingIndicator />}
            </div>
          </>
        )}
      </div>

      {/* Input */}
      <div className="px-4 py-4 border-t border-gray-100 bg-gray-50 flex-shrink-0">
        {error && <p className="text-xs text-red-500 mb-2 text-center">{error}</p>}
        <div className={`flex items-center gap-2 bg-white border border-gray-200 shadow-sm p-2.5 pl-3 rounded transition-shadow ${isLoading ? '' : 'focus-within:ring-1 focus-within:ring-indigo-500 focus-within:border-indigo-500'}`}>
          <button
            type="button"
            onClick={clearChat}
            disabled={isLoading || messages.length === 0}
            title="Clear chat"
            className="flex-shrink-0 w-8 h-8 rounded flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Trash2Icon className="h-4 w-4" />
          </button>
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask me anything"
            disabled={isLoading}
            className="flex-1 text-sm text-gray-800 bg-transparent outline-none placeholder-gray-400 disabled:cursor-wait"
          />
          <button
            onClick={() => sendMessage(inputValue)}
            disabled={isLoading || !inputValue.trim()}
            className="flex-shrink-0 w-8 h-8 rounded bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clipPath="url(#clip0_send)">
                <path d="M13.3352 7.845L6.16271 15.0175L4.98438 13.8392L12.156 6.66667H5.83521V5H15.0019V14.1667H13.3352V7.845V7.845Z" fill="white" />
              </g>
              <defs>
                <clipPath id="clip0_send">
                  <rect width="20" height="20" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIAssistantChat;
